import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { StatusMessage } from '../components/StatusMessage';
import { useChannels } from '../providers/ChannelProvider';
import * as tuningInstructionRepository from '../repositories/tuningInstructionRepository';

export default function TuningPage() {
  const { channelId } = useParams<{ channelId: string }>();
  const { channels } = useChannels();
  const [channelName, setChannelName] = useState(channelId || 'Channel');
  const [text, setText] = useState('');
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ message: '', isError: false });

  useEffect(() => {
    if (!channelId) return;
    const found = channels.find((c) => c.id === channelId);
    if (found) setChannelName(found.name);

    tuningInstructionRepository.getInstruction(channelId).then((inst) => {
      if (inst) {
        setText(inst.text);
        setExists(true);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [channelId, channels]);

  const handleSave = async () => {
    if (!channelId) return;
    try {
      await tuningInstructionRepository.saveInstruction(channelId, text);
      setExists(true);
      setStatus({ message: 'Instruction saved', isError: false });
    } catch (err) {
      setStatus({ message: (err as Error).message || 'Failed to save', isError: true });
    }
  };

  const handleDelete = async () => {
    if (!channelId || !window.confirm('Delete this instruction?')) return;
    try {
      await tuningInstructionRepository.deleteInstruction(channelId);
      setText('');
      setExists(false);
      setStatus({ message: 'Instruction deleted', isError: false });
    } catch (err) {
      setStatus({ message: (err as Error).message || 'Failed to delete', isError: true });
    }
  };

  return (
    <div className="admin-shell">
      <header className="admin-header">
        <div>
          <div className="pane-eyebrow">Settings</div>
          <h1>Tuning: {channelName}</h1>
          <p>Set a tuning instruction for this channel.</p>
        </div>
        <div className="admin-header-actions">
          <Link to="/tldr" className="ghost-button nav-link-button">Back to reader</Link>
        </div>
      </header>
      <StatusMessage message={status.message} isError={status.isError} />
      {loading ? (
        <div className="empty-panel">Loading...</div>
      ) : (
        <section className="admin-panel site-form tuning-form">
          <div className="form-field-group">
            <label>
              <span>Prompt text</span>
              <textarea
                className="tuning-prompt-textarea"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="e.g. Focus on technical details, ignore marketing fluff, highlight any code examples..."
              />
            </label>
            <p className="field-help">
              These instructions guide the AI when it summarizes and analyzes stories for this channel. 
              Be specific about what kind of content you want to see and what should be prioritized.
            </p>
          </div>
          <div className="site-card-actions panel-footer">
            <button className="primary-button" onClick={handleSave} style={{ minWidth: '120px' }}>Save changes</button>
            {exists && (
              <button className="ghost-button danger-button" onClick={handleDelete}>Delete instruction</button>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
